'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Plus,
  Crown,
  Shield,
  User,
  ArrowRight,
  Spinner,
  Lightning,
  ArrowLeft,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Dialog } from '../components/ui/Dialog';
import { useToastActions } from '../components/ui/Toast';
import { teamApi } from '../api/team';
import type { Team } from '../types/team';
import { useTeamStore } from '../stores/teamStore';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { getRegionProfile } from '../config/region';
import { getTeamPurchaseUrl } from '../config/purchase';

interface TeamListPageProps {
  onBack?: () => void;
}

export function TeamListPage({ onBack }: TeamListPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToastActions();
  const { setCurrentTeamId } = useTeamStore();
  const { subscription } = useSubscriptionStore();
  const profile = getRegionProfile();
  const isCN = profile.region === 'cn';

  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // 检查用户是否可以创建团队（免费版不能创建）
  const canCreateTeam = () => {
    return subscription?.plan && subscription.plan !== 'basic' && subscription.plan !== 'free';
  };

  // 获取团队列表
  const fetchTeams = async () => {
    try {
      setIsLoading(true);
      const teamRes = await teamApi.listTeams();
      const teamsList = teamRes.data?.teams ?? [];
      setTeams(teamsList);
    } catch (error) {
      showError(t('team.fetchError'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  // 处理创建团队按钮点击
  const handleCreateTeamClick = () => {
    if (!canCreateTeam()) {
      setIsUpgradeDialogOpen(true);
      return;
    }
    setIsCreateDialogOpen(true);
  };

  // 跳转到升级页面
  const handleUpgrade = () => {
    setIsUpgradeDialogOpen(false);
    if (isCN) {
      window.open(getTeamPurchaseUrl(), '_blank');
    } else {
      navigate('/upgrade');
    }
  };

  // 创建团队
  const handleCreateTeam = async () => {
    if (!createName.trim()) {
      showError(t('team.nameRequired'));
      return;
    }
    try {
      setIsCreating(true);
      const res = await teamApi.createTeam({ 
        name: createName.trim(), 
        description: createDescription.trim() 
      });
      if (res.code === 0 && res.data) {
        showSuccess(t('team.createSuccess'));
        setCurrentTeamId(res.data.id);
        setCreateName('');
        setCreateDescription('');
        setIsCreateDialogOpen(false);
        // 创建后跳转到团队详情
        navigate(`/team/${res.data.id}`);
      } else {
        showError(t('team.createError'));
      }
    } catch (e) {
      showError(t('team.createError'));
    } finally {
      setIsCreating(false);
    }
  };

  // 点击团队进入详情
  const handleTeamClick = (teamId: string) => {
    setCurrentTeamId(teamId);
    navigate(`/team/${teamId}`);
  };

  // 获取角色图标
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="w-4 h-4 text-amber-500" weight="fill" />;
      case 'admin':
        return <Shield className="w-4 h-4 text-blue-500" weight="fill" />;
      default:
        return <User className="w-4 h-4 text-zinc-400" weight="fill" />;
    }
  };

  // 获取角色标签
  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner':
        return t('team.roleOwner');
      case 'admin':
        return t('team.roleAdmin');
      default:
        return t('team.roleMember');
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ type: 'spring', stiffness: 100, damping: 20 }}
      className="h-full bg-zinc-50 overflow-auto"
    >
      <div className="max-w-3xl mx-auto p-6">
        {/* 返回按钮和标题 */}
        <div className="mb-8">
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </button>
          )}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">{t('settings.myTeam')}</h1>
              <p className="text-sm text-zinc-500 mt-1">{t('team.myTeamsDesc') || '管理您的团队和成员'}</p>
            </div>
            <Button
              onClick={handleCreateTeamClick}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('team.createTeam')}
            </Button>
          </div>
        </div>

        {/* 团队列表 */}
        {teams.length === 0 ? (
          <div className="bg-white rounded-2xl border border-zinc-200/50 p-12 text-center">
            <Users className="w-14 h-14 text-zinc-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-zinc-900 mb-2">{t('team.noTeamYet') || '您还没有团队'}</h2>
            <p className="text-sm text-zinc-500 mb-6 max-w-sm mx-auto">
              {t('team.noTeamHint') || '创建团队后即可邀请成员、共享工作流。'}
            </p>
            <Button onClick={handleCreateTeamClick} className="gap-2">
              <Plus className="w-4 h-4" />
              {t('team.createTeam') || '创建团队'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {teams.map((team) => (
              <div
                key={team.id}
                onClick={() => handleTeamClick(team.id)}
                className="bg-white rounded-2xl border border-zinc-200/50 p-6 hover:shadow-md hover:border-zinc-300 transition-all cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-indigo-600" weight="fill" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-zinc-900">{team.name}</h3>
                      <p className="text-sm text-zinc-500">{team.description || t('team.noDescription')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* 用户在该团队的角色 */}
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-100 text-xs font-medium text-zinc-600">
                      {getRoleIcon(team.currentUserRole || 'member')}
                      {getRoleLabel(team.currentUserRole || 'member')}
                    </div>
                    <ArrowRight className="w-5 h-5 text-zinc-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 创建团队对话框 */}
      <Dialog
        isOpen={isCreateDialogOpen}
        onClose={() => { 
          setIsCreateDialogOpen(false); 
          setCreateName(''); 
          setCreateDescription(''); 
        }}
        title={t('team.createTeam') || '创建团队'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">
              {t('team.teamName') || '团队名称'}
            </label>
            <Input
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder={t('team.teamNamePlaceholder') || '输入团队名称'}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">
              {t('team.description') || '描述'}
            </label>
            <Input
              value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              placeholder={t('team.descriptionPlaceholder') || '选填'}
              className="w-full"
            />
          </div>
          <Button 
            onClick={handleCreateTeam} 
            disabled={isCreating || !createName.trim()} 
            className="w-full"
          >
            {isCreating ? <Spinner className="w-4 h-4 animate-spin" /> : (t('team.createTeam') || '创建团队')}
          </Button>
        </div>
      </Dialog>

      {/* 升级提示对话框 */}
      <Dialog
        isOpen={isUpgradeDialogOpen}
        onClose={() => setIsUpgradeDialogOpen(false)}
        title={t('team.upgradeRequired') || '需要升级'}
      >
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Lightning className="w-8 h-8 text-amber-600" weight="fill" />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">
              {t('team.upgradeToCreateTeam') || '升级以创建团队'}
            </h3>
            <p className="text-sm text-zinc-500 max-w-sm mx-auto">
              {t('team.upgradeHint') || '免费版用户无法创建团队。升级到专业版或高级版，即可创建团队并邀请成员协作。'}
            </p>
          </div>
          
          <div className="bg-zinc-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-3 text-sm text-zinc-600">
              <Crown className="w-5 h-5 text-amber-500" weight="fill" />
              <span>{t('team.proFeature1') || '创建团队并邀请成员'}</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-zinc-600">
              <Crown className="w-5 h-5 text-amber-500" weight="fill" />
              <span>{t('team.proFeature2') || '共享工作流和模板'}</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-zinc-600">
              <Crown className="w-5 h-5 text-amber-500" weight="fill" />
              <span>{t('team.proFeature3') || '团队协作'}</span>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => setIsUpgradeDialogOpen(false)}
              className="flex-1"
            >
              {t('common.cancel') || '取消'}
            </Button>
            <Button
              onClick={handleUpgrade}
              className="flex-1 gap-2"
            >
              {isCN ? t('team.buyActivationCode') : t('team.upgradeNow')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Dialog>
    </motion.div>
  );
}
